// @ts-strict-ignore
import {ChartTypeDisplayNameKey, RESULTS_OVERALL_SUM_KEY} from 'common/report/constants';
import {ChartType} from 'common/types/reports/bookmark';
import {ProjectTimezone} from 'common/report/stores/types';
import {
  LiftType,
  LiftTypeDisabledKey,
  LiftTypeEnabledState,
  SegmentLiftBaselineOption,
} from 'common/report/util/lift/types';
import {getTimeseriesFromTimeComparisonValues, memoizedGetHighchartsTimestamp} from 'common/report/insights/util';
import {LiftComparison} from 'common/widgets/lift-comparison-menu/types';
import {CohortSegment} from 'common/query-builder/query-block/segment-menu/types';
import {formatMetricNumber} from 'common/util';
import {Translatable} from 'common/i18n';

export function getLiftBaselineSegment<T>(
  liftComparison: LiftComparison,
  segmentPath: string[],
  segmentLookup: Map<string, T>,
  timeComparisonSegment: T,
): T {
  let segmentLookupKey;
  switch (liftComparison?.type) {
    case LiftType.Overall: {
      const higherLevelPath = segmentPath.slice(0, segmentPath.length - 1);
      segmentLookupKey = higherLevelPath.concat([RESULTS_OVERALL_SUM_KEY]).join(`,`);
      break;
    }
    case LiftType.Segment:
      segmentLookupKey = liftComparison?.groupValue;
      break;
    case LiftType.Time:
      return timeComparisonSegment;
    default:
      return null;
  }

  // didn't return in the case of Baseline or Overall:
  if (segmentLookup.has(segmentLookupKey)) {
    // single metric
    return segmentLookup.get(segmentLookupKey);
  } else {
    // multi-metric
    const baselinePath = segmentPath.slice(0, segmentPath.length - 1);
    baselinePath.push(segmentLookupKey);
    const lookupKey = baselinePath.join(`,`);
    return segmentLookup.get(lookupKey);
  }
}

export function getLiftComparisonValues<T>(
  {
    segment,
    comparisonSegment,
    liftType,
    projectTimezone,
  }: {
    segment: T;
    comparisonSegment: T;
    liftType: LiftType;
    projectTimezone: ProjectTimezone;
  },
  segmentValueGetter: (segment: T) => Record<string, number> = (seg) => (seg as any).value,
): (timestamp: number) => number {
  if (!comparisonSegment) {
    // there are a lot of legitimate reasons we may not have one
    // not the least of which is two events w/ the same properties,
    // but one event has an inline filter excluding the segment we are trying to use as a baseline
    return () => 0;
  }

  function getIsoDatesToTimestampMap(isoDates: string[]): Map<number, string> {
    return new Map(
      isoDates.map((isoDate) => [
        memoizedGetHighchartsTimestamp(isoDate, projectTimezone.timezone, projectTimezone.utcOffset),
        isoDate,
      ]),
    );
  }

  switch (liftType) {
    case LiftType.Time: {
      const compositeTimeseries = getTimeseriesFromTimeComparisonValues(
        segmentValueGetter(segment),
        segmentValueGetter(comparisonSegment),
        false,
      );
      const isoDatesToValues = getIsoDatesToTimestampMap(Object.keys(compositeTimeseries));
      return (timestamp: number) => compositeTimeseries[isoDatesToValues.get(timestamp)];
    }
    case LiftType.Overall:
    case LiftType.Segment: {
      const isoDatesToValues = getIsoDatesToTimestampMap(Object.keys(segmentValueGetter(segment)));
      return (timestamp: number) => segmentValueGetter(comparisonSegment)[isoDatesToValues.get(timestamp)];
    }
  }
}

/*
 * Common logic to turn CohortSegment[] into options that can be used to build a proper LiftComparison
 * Deals with labeling the cohorts based on negated or not.
 */
export function generateCohortBaselineOptions(cohortSegments: CohortSegment[]): SegmentLiftBaselineOption[] {
  return cohortSegments
    .map((cohortSegment) => {
      const displayName = (cohortSegment.cohort.negated ? `Not In ` : ``) + cohortSegment.cohort.name;
      return {cohortSegment, displayName};
    })
    .flat();
}

export function message(liftType: LiftType, chartType: ChartType, reason?: Translatable): Translatable {
  return ({t, print}) =>
    t(LiftTypeDisabledKey[liftType], {
      chartType: reason
        ? print(reason)
        : t(`reports.chart-types.labels.chart`, {chartType: t(ChartTypeDisplayNameKey[chartType])}),
    });
}

export function disableLiftTypeForChart(
  liftType: LiftType,
  chartType: ChartType,
  reason?: Translatable,
): LiftTypeEnabledState {
  return {
    enabled: false,
    reason: message(liftType, chartType, reason),
  };
}

/*
 * 0.1245 => 12.45%; 1000 => 100k%
 */
export function formatLiftPercent(liftValue: number) {
  const percent = Math.round(liftValue * Math.pow(10, 2 + 2)) / Math.pow(10, 2);
  return (liftValue > 0 ? `+` : ``) + formatMetricNumber(percent) + `%`;
}
